import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Artist,
    ArtistSimple,
    getArtists,
    getSpotifyArtist,
    patchArtist,
    refreshArtistFromSpotify,
    uploadArtistImage,
} from '@round/api';
import { mapApiErrorsToFormikErrors } from '../../../../utility/utility';
import { showNotification } from '../../../../helpers';
import ArtistForm, { ArtistFormProps } from '../../components/ArtistForm/ArtistForm';
import Button from '../../../../ui/Buttons/Button/Button';
import Modal, { ModalContent, ModalTitle } from '../../../../ui/Layout/Modals/Modal/Modal';
import useDownloadImage from '../../../../Hooks/useDownloadImage';
import { GenericDropdownOption } from '../../../../App.types';
import styles from './UpdateArtistModal.module.css';
import ArtistFromSpotifyForm, {
    ArtistFromSpotifyFormProps,
} from '../../components/ArtistFromSpotifyForm/ArtistFromSpotifyForm';

type UpdateArtistModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (artist: Artist) => void;
    onRefreshed?: (artist: ArtistSimple) => void;
    artist: Artist | null;
};

const UpdateArtistModal = ({ onSubmit, onClose, isOpen, artist, onRefreshed }: UpdateArtistModalProps) => {
    const [isFormManual, setIsFormManual] = useState(true);
    const [isRefreshFromSpotifyLoading, setIsRefreshFromSpotifyLoading] = useState(false);

    const { image } = useDownloadImage(artist?.image);
    const [relatedArtists, setRelatedArtists] = useState<Artist[]>([]);
    const [spotifyArtist, setSpotifyArtist] = useState<GenericDropdownOption<number> | null>(null);

    const manualFormInitialValues: ArtistFormProps['initialValues'] = useMemo(
        () => ({
            name: artist?.name ?? '',
            spotify_artist: spotifyArtist,
            new_image: image,
            related_artists: relatedArtists
                .filter((a) => artist?.related_artists?.includes(a.id))
                .map((a) => ({
                    value: a.id,
                    label: a.name,
                    image: a.image,
                })),
        }),
        [artist?.name, artist?.related_artists, image, relatedArtists, spotifyArtist]
    );

    const spotifyFormInitialValues: ArtistFromSpotifyFormProps['initialValues'] = useMemo(
        () => ({
            artist: artist ? { value: artist.id, label: artist.name } : null,
            url: '',
        }),
        [artist]
    );

    useEffect(() => {
        if (artist) {
            getArtists({ page: 1, page_size: artist.related_artists.length, id: artist.related_artists.join(',') })
                .then((response) => {
                    if (response.status === 200) {
                        setRelatedArtists(response.data.results);
                    }
                })
                .catch(() => {});
        }
    }, [artist]);

    useEffect(() => {
        if (typeof artist?.spotify_artist === 'number') {
            getSpotifyArtist(artist.spotify_artist)
                .then((response) => {
                    if (response.status === 200) {
                        setSpotifyArtist({
                            label: response.data.name,
                            value: response.data.id,
                        });
                    }
                })
                .catch(() => {});
        }
    }, [artist?.spotify_artist]);

    useEffect(() => {
        if (!isOpen) {
            setRelatedArtists([]);
            setSpotifyArtist(null);
            setIsFormManual(true);
        }
    }, [isOpen]);

    const handleManualFormSubmit: ArtistFormProps['handleSubmit'] = useCallback(
        async (values, helpers) => {
            if (!artist) {
                return;
            }

            try {
                helpers.setSubmitting(true);
                let response = await patchArtist(artist.id, {
                    name: values.name,
                    spotify_artist: values.spotify_artist?.value ?? null,
                    related_artists: values.related_artists?.map((a) => a.value) ?? [],
                });

                if (response.status === 400) {
                    helpers.setErrors(mapApiErrorsToFormikErrors(response.data));
                    return;
                }

                if (values.new_image) {
                    response = await uploadArtistImage(response.data.id, values.new_image);
                }

                onSubmit(response.data);
                showNotification('Artist updated', 'info');
            } catch {
                showNotification("Sorry, we're experiencing technical issues", 'error');
            } finally {
                helpers.setSubmitting(false);
            }
        },
        [artist, onSubmit]
    );

    const handleRefreshSpotifyData = useCallback(async () => {
        if (!artist || typeof artist?.spotify_artist !== 'number') {
            return;
        }

        try {
            setIsRefreshFromSpotifyLoading(true);
            const response = await refreshArtistFromSpotify(artist.id);
            if (response.status === 404) {
                showNotification('Artist not found', 'error');
                return;
            }

            if (response.status === 400) {
                showNotification(response.data.error, 'error');
                return;
            }

            if (typeof onRefreshed === 'function') {
                onRefreshed(response.data);
            }

            showNotification('Refreshed', 'info');
        } catch {
            showNotification('Could not refresh data from spotify', 'error');
        } finally {
            setIsRefreshFromSpotifyLoading(false);
        }
    }, [artist, onRefreshed]);

    const handleSpotifyFormSubmit: ArtistFromSpotifyFormProps['handleSubmit'] = useCallback(
        async (values, { setSubmitting }) => {
            if (!values.artist || typeof artist?.spotify_artist === 'number') {
                return;
            }

            try {
                setSubmitting(true);
                const response = await refreshArtistFromSpotify(values.artist.value, values.url);
                if (response.status === 404) {
                    showNotification('Artist not found', 'error');
                    return;
                }

                if (response.status === 400) {
                    showNotification(response.data.error, 'error');
                    return;
                }

                if (typeof onRefreshed === 'function') {
                    onRefreshed(response.data);
                }

                setIsFormManual(true);
                showNotification('Linked', 'info');
            } catch {
                showNotification('Could not link to spotify artist', 'error');
            } finally {
                setSubmitting(false);
            }
        },
        [artist?.spotify_artist, onRefreshed]
    );

    return (
        <Modal closeOnOverlayClick isOpen={isOpen} onClose={onClose}>
            <ModalTitle>Update Artist</ModalTitle>
            <ModalContent>
                {isFormManual && (
                    <ArtistForm
                        initialValues={manualFormInitialValues}
                        handleSubmit={handleManualFormSubmit}
                        renderActions={({ isSubmitting, submitForm }) => (
                            <div className={styles.actions}>
                                <Button type="filled" color="black" onClick={submitForm} loading={isSubmitting}>
                                    Save
                                </Button>

                                {typeof artist?.spotify_artist === 'number' && (
                                    <Button
                                        type="bordered"
                                        onClick={handleRefreshSpotifyData}
                                        loading={isRefreshFromSpotifyLoading}
                                    >
                                        Refresh from spotify
                                    </Button>
                                )}

                                {typeof artist?.spotify_artist !== 'number' && (
                                    <Button type="link" onClick={() => setIsFormManual(false)}>
                                        Link to spotify artist
                                    </Button>
                                )}
                            </div>
                        )}
                    />
                )}

                {!isFormManual && (
                    <ArtistFromSpotifyForm
                        disabledFields={['artist']}
                        initialValues={spotifyFormInitialValues}
                        handleSubmit={handleSpotifyFormSubmit}
                        renderActions={({ submitForm, isSubmitting, isValid }) => (
                            <div className={styles.actions}>
                                <Button
                                    type="filled"
                                    color="black"
                                    onClick={submitForm}
                                    loading={isSubmitting}
                                    disabled={!isValid}
                                >
                                    Submit
                                </Button>

                                <Button type="link" onClick={() => setIsFormManual(true)}>
                                    Edit manually
                                </Button>
                            </div>
                        )}
                    />
                )}
            </ModalContent>
        </Modal>
    );
};

export default UpdateArtistModal;
